import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  ACCESS,
  ACCESS_LABEL,
  EMP_LEVELS,
  MAX_ACTIVE_FINANCE_HOLDERS,
  type AccessLevel,
  type EmploymentLevel,
  type User,
} from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/components/Toast';
import {
  ASSIGNABLE_ACCESS,
  departmentsOf,
  financeSeatsTaken,
  looksLikeEmail,
} from './helpers';

export interface AddEmployeeModalProps {
  users: readonly User[];
  onClose: () => void;
  onChanged: () => Promise<void>;
}

export function AddEmployeeModal({ users, onClose, onChanged }: AddEmployeeModalProps): ReactNode {
  const { toast } = useToast();
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [empId, setEmpId] = useState('');
  const [email, setEmail] = useState('');
  const [department, setDepartment] = useState('');
  const [position, setPosition] = useState('');
  const [employmentLevel, setEmploymentLevel] = useState<EmploymentLevel>('Rank & File');
  const [reportsTo, setReportsTo] = useState('');
  const [systemAccess, setSystemAccess] = useState<AccessLevel>(ACCESS.REQUESTOR);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const departments = useMemo(() => departmentsOf(users), [users]);
  const managerOptions = useMemo(() => users.filter((u) => u.active), [users]);
  const financeSeatsFull = financeSeatsTaken(users) >= MAX_ACTIVE_FINANCE_HOLDERS;

  const submit = useCallback(async (): Promise<void> => {
    const found: string[] = [];
    if (!first.trim() || !last.trim()) found.push('First and last name are required.');
    if (!empId.trim()) found.push('Employee ID is required.');
    if (!email.trim()) found.push('Company email is required.');
    else if (!looksLikeEmail(email.trim())) found.push('That does not look like a valid email address.');
    if (!department.trim()) found.push('Department is required.');
    if (!position.trim()) found.push('Position is required.');
    setErrors(found);
    if (found.length > 0) return;

    setBusy(true);
    try {
      const created = await api.admin.addUser<User>({
        empId: empId.trim(),
        name: `${first.trim()} ${last.trim()}`,
        email: email.trim().toLowerCase(),
        department: department.trim(),
        position: position.trim(),
        employmentLevel,
        systemAccess,
        reportsTo: reportsTo || null,
      });
      await onChanged();
      toast(`${created.name} added to ${created.department}.`, 'ok');
      onClose();
    } catch (err) {
      setErrors([errorMessage(err)]);
      setBusy(false);
    }
  }, [
    department,
    email,
    empId,
    employmentLevel,
    first,
    last,
    onChanged,
    onClose,
    position,
    reportsTo,
    systemAccess,
    toast,
  ]);

  return (
    <Modal title="Add Employee" onClose={busy ? () => undefined : onClose} wide>
      <div className="form-row">
        <div className="field">
          <label htmlFor="ne-first">First Name</label>
          <input id="ne-first" value={first} onChange={(e) => setFirst(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="ne-last">Last Name</label>
          <input id="ne-last" value={last} onChange={(e) => setLast(e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="ne-empid">Employee ID</label>
          <input
            id="ne-empid"
            value={empId}
            placeholder="e.g. GDEC-0099"
            onChange={(e) => setEmpId(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="ne-email">Company Email</label>
          <input
            id="ne-email"
            value={email}
            placeholder="name@gdec.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="ne-dept">Department</label>
          <input
            id="ne-dept"
            list="ne-dept-list"
            value={department}
            placeholder="e.g. Commercial"
            onChange={(e) => setDepartment(e.target.value)}
          />
          <datalist id="ne-dept-list">
            {departments.map((d) => (
              <option key={d} value={d} />
            ))}
          </datalist>
        </div>
        <div className="field">
          <label htmlFor="ne-position">Position</label>
          <input
            id="ne-position"
            value={position}
            placeholder="e.g. Associate"
            onChange={(e) => setPosition(e.target.value)}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="ne-level">Rank / Employment Level</label>
          <select
            id="ne-level"
            value={employmentLevel}
            onChange={(e) => setEmploymentLevel(e.target.value as EmploymentLevel)}
          >
            {EMP_LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <p className="field-note">
            Executive rank sits at the top of the organization chart and skips Manager/Lead approval
            on expense claims.
          </p>
        </div>
        <div className="field">
          <label htmlFor="ne-reports-to">Reports To</label>
          <select id="ne-reports-to" value={reportsTo} onChange={(e) => setReportsTo(e.target.value)}>
            <option value="">— (top of chart)</option>
            {managerOptions.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} — {u.department}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor="ne-access">System Access</label>
        <select
          id="ne-access"
          value={systemAccess}
          onChange={(e) => setSystemAccess(e.target.value as AccessLevel)}
        >
          {ASSIGNABLE_ACCESS.map((a) => (
            <option
              key={a}
              value={a}
              disabled={a === ACCESS.FINANCE_REVIEW && financeSeatsFull}
            >
              {ACCESS_LABEL[a]}
            </option>
          ))}
        </select>
        <p className="field-note">
          Main Access cannot be granted here — it moves only through a confirmed transfer.
        </p>
      </div>

      {errors.length > 0 ? (
        <div className="error-text">
          {errors.map((e) => (
            <div key={e}>• {e}</div>
          ))}
        </div>
      ) : null}

      <div className="cf-submitbar">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={busy}>
          {busy ? <Spinner /> : null}
          Save Employee
        </button>
      </div>
    </Modal>
  );
}
